import { AxiosError } from "axios";
import { useQuery, QueryObserverResult, UseQueryOptions } from "react-query";

import {
  FetcherQueryKey,
  computeIsQueryEnabled,
} from "../../../services/query";
import { MembersResponse, MembersDataEnvelope } from "../../../models/members";

import { useMaybeUserData } from "../user";
import { useMaybeCurrentOperationData } from "../operations";
import {
  useMaybeFabricFeatureFlag,
  useMaybeFeatureFlag,
} from "../featureFlags";

import {
  MembersFetcherParams,
  membersQueryKeyPrefix,
  computeMembersQueryKey,
  membersFetcher,
  isDataEqual,
  computeResultOfUseQuery,
  onSuccess,
  onError,
  useHandleLoadingStatus,
} from "./members.query.helpers";
import { useSetMembersGlobalActivityAndStatus } from "./members.globalStatus";

export { membersQueryKeyPrefix, computeMembersQueryKey };

// ########################
// ## useMembersQuery ##
// ########################

export type ParamsOfUseMembersQuery = {
  watch?: UseQueryOptions["notifyOnChangeProps"];
  forceDisable?: boolean;
};
export type ResultOfUseMembersQuery = QueryObserverResult<
  MembersDataEnvelope,
  AxiosError
>;
export type UseMembersQuery = (
  params?: ParamsOfUseMembersQuery,
) => ResultOfUseMembersQuery;

/**
 * The returned value of this hook will contain the React Query result
 * of the fetching of the members data.
 *
 * This hook is only meant for use by the hooks in `./members.ts`.
 * Do not use this hook outside these hooks as this could couple us to React Query.
 */
export const useMembersQuery: UseMembersQuery = ({
  watch,
  forceDisable,
} = {}) => {
  const user = useMaybeUserData();
  const operation = useMaybeCurrentOperationData();
  const catalogFlag = useMaybeFabricFeatureFlag({
    key: "roles-api",
  });

  const isAarEnabled = useMaybeFeatureFlag({
    app: "fabric",
    key: "settings-aar-role",
  });

  const setMembersGlobalActivityAndStatus =
    useSetMembersGlobalActivityAndStatus();

  const params: MembersFetcherParams = {
    userId: user?.granularId,
    operationId: operation?.id,
    isAarEnabled,
    catalogFlag,
  };
  const resultOfUseQuery = useQuery<
    MembersResponse,
    AxiosError,
    MembersResponse,
    FetcherQueryKey<MembersFetcherParams>
  >(computeMembersQueryKey(params), membersFetcher, {
    enabled: computeIsQueryEnabled(params, { forceDisable }),
    notifyOnChangeProps: watch || "tracked",
    isDataEqual,
    onSuccess: () => onSuccess({ setMembersGlobalActivityAndStatus }),
    onError: () => onError({ setMembersGlobalActivityAndStatus }),
  });

  useHandleLoadingStatus({
    resultOfUseQuery,
    setMembersGlobalActivityAndStatus,
  });

  return computeResultOfUseQuery(
    resultOfUseQuery,
    user,
    operation?.permissions,
  );
};
